<template>
  <div class="rts-day-detail">
    <div class="rts-toolbar">
      <div class="rts-toolbar-left">
        <button class="rts-btn-back" @click="$emit('back')">
          <i class="fas fa-arrow-left"></i> Back to Week
        </button>
        <span class="rts-badge"
              :class="orderType === 'home_delivery' ? 'rts-badge-hd' : 'rts-badge-sp'">
          <i :class="orderType === 'home_delivery' ? 'fas fa-truck' : 'fas fa-store'"></i>
          {{ orderType === 'home_delivery' ? 'HOME DELIVERY' : 'STORE PICKUP' }}
        </span>
        <span class="rts-week-label">{{ dayShort }} · {{ dateLabel }}</span>
      </div>
      <div class="rts-toolbar-right">
        <button class="rts-nav-btn" @click="$emit('prev')">
          <i class="fas fa-chevron-left"></i> Prev Day
        </button>
        <button class="rts-nav-btn" @click="$emit('next')">
          Next Day <i class="fas fa-chevron-right"></i>
        </button>
        <button class="rts-nav-btn rts-refresh" @click="$emit('refresh')" title="Refresh">
          <i class="fas fa-sync"></i>
        </button>
      </div>
    </div>

    <div class="rts-stats-grid">
      <div class="rts-stat-card rts-stat-blue">
        <div class="rts-stat-icon"><i class="fas fa-clipboard-list"></i></div>
        <div class="rts-stat-body">
          <div class="rts-stat-value">{{ safeInvoices.length }}</div>
          <div class="rts-stat-label">Total Orders</div>
        </div>
      </div>
      <div class="rts-stat-card rts-stat-green">
        <div class="rts-stat-icon"><i class="fas fa-check-circle"></i></div>
        <div class="rts-stat-body">
          <div class="rts-stat-value">{{ assignedTotal }}</div>
          <div class="rts-stat-label">Assigned</div>
        </div>
      </div>
      <div class="rts-stat-card" :class="unassignedTotal > 0 ? 'rts-stat-red' : 'rts-stat-gray'">
        <div class="rts-stat-icon"><i class="fas fa-inbox"></i></div>
        <div class="rts-stat-body">
          <div class="rts-stat-value">{{ unassignedTotal }}</div>
          <div class="rts-stat-label">Unassigned</div>
        </div>
      </div>
      <div class="rts-stat-card rts-stat-teal">
        <div class="rts-stat-icon"><i class="fas fa-weight-hanging"></i></div>
        <div class="rts-stat-body">
          <div class="rts-stat-value">{{ dayAllowed }}</div>
          <div class="rts-stat-label">Day Capacity</div>
        </div>
      </div>
    </div>

    <div class="rts-day-actions">
      <button class="rts-action-btn rts-action-distribute" @click="$emit('distribute', day)">
        <i class="fas fa-random"></i> Distribute / Rebalance Day
      </button>
    </div>

    <div class="rts-zone-breakdown">
      <div
        v-for="z in zoneBreakdown"
        :key="z.zone_id"
        class="rts-zone-card"
        :class="zoneCardClass(z)"
      >
        <div class="rts-zone-card-head" :style="{ background: z.color || '#999' }">
          <span class="rts-zone-code">{{ z.code }}</span>
          <span class="rts-zone-name">{{ z.name }}</span>
          <span class="rts-zone-cap" v-if="z.max > 0">{{ z.scheduled }} / {{ z.max }}</span>
          <span class="rts-zone-cap" v-else>{{ z.scheduled }} / ∞</span>
        </div>
        <div class="rts-zone-card-body">
          <div class="rts-zone-status" :class="z.statusClass">{{ z.statusLabel }}</div>
          <div v-if="z.overflow > 0" class="rts-zone-overflow">
            ⚠ {{ z.overflow }} over capacity
          </div>
          <div class="rts-zone-orders">
            <div v-for="inv in z.invoices" :key="inv.invoice_id" class="rts-order-mini">
              <div class="rts-order-mini-top">
                <span class="rts-order-mini-inv">#{{ inv.invoice_id }}</span>
                <span v-if="inv.assignment_type" class="rts-order-mini-type">
                  {{ inv.assignment_type }}
                </span>
              </div>
              <div class="rts-order-mini-name">{{ inv.fullname || '—' }}</div>
              <div class="rts-order-mini-addr">
                {{ inv.address_line_1 || '—' }}<span v-if="inv.city">, {{ inv.city }}</span>
              </div>
              <button class="rts-order-mini-resched" @click="$emit('reschedule', inv)">
                <i class="fas fa-calendar-alt"></i> Reschedule
              </button>
            </div>
            <div v-if="!z.invoices.length" class="rts-zone-empty">
              No orders in this zone
            </div>
          </div>
        </div>
      </div>
      <div v-if="!zoneBreakdown.length" class="rts-zone-card rts-zone-card-empty">
        <div class="rts-zone-card-body">
          <i class="fas fa-inbox"></i>
          <div>No orders scheduled for this day</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DayDetail',
  props: {
    // Relaxed: no `required: true` so a stale prop won't crash.
    day:        { type: Object, default: () => ({}) },
    orderType:  { type: String, default: '' },
    invoices:   { type: Array,  default: () => [] },
    zoneCounts: { type: Object, default: () => ({}) },
    capacity:   { type: Object, default: () => ({}) },
  },
  computed: {
    dayShort() {
      return (this.day && this.day.dayShort) ? this.day.dayShort : ''
    },
    dateLabel() {
      return (this.day && this.day.dateLabel) ? this.day.dateLabel : ''
    },
    safeInvoices() {
      return Array.isArray(this.invoices) ? this.invoices : []
    },
    assignedTotal() {
      let n = 0
      const zc = this.zoneCounts || {}
      Object.values(zc).forEach(z => { n += ((z && z.assigned) || 0) })
      return n
    },
    unassignedTotal() {
      return Math.max(0, this.safeInvoices.length - this.assignedTotal)
    },
    dayAllowed() {
      let t = 0
      const cap = this.capacity || {}
      Object.keys(cap).forEach(zid => {
        const c = cap[zid]
        if (c && c.is_active) t += (c.max_orders || 0)
      })
      return t
    },
    zoneBreakdown() {
      const byZone = {}
      this.safeInvoices.forEach(inv => {
        if (!inv) return
        const zid = inv.zone_id || 0
        if (!byZone[zid]) {
          byZone[zid] = {
            zone_id:  zid,
            code:     inv.zone_code  || '—',
            name:     inv.zone_name  || 'Unzoned',
            color:    inv.zone_color || '#a0aec0',
            invoices: [],
          }
        }
        byZone[zid].invoices.push(inv)
      })
      return Object.values(byZone).map(z => {
        const counts = (this.zoneCounts && this.zoneCounts[z.zone_id]) || {}
        const cap    = (this.capacity   && this.capacity[z.zone_id])   || { is_active: true, max_orders: 0 }
        const scheduled = (counts.scheduled != null) ? counts.scheduled : z.invoices.length
        const max = cap.is_active ? (cap.max_orders || 0) : 0
        const overflow = (max > 0) ? Math.max(0, scheduled - max) : 0
        let statusClass = 'rts-zone-ok'
        let statusLabel = 'OK'
        if (!cap.is_active)                    { statusClass = 'rts-zone-off';       statusLabel = 'ZONE OFF' }
        else if (overflow > 0)                 { statusClass = 'rts-zone-over';      statusLabel = 'OVER CAPACITY' }
        else if (max > 0 && scheduled === max) { statusClass = 'rts-zone-full';      statusLabel = 'FULL' }
        else if (max === 0)                    { statusClass = 'rts-zone-unlimited'; statusLabel = 'UNLIMITED' }
        return Object.assign({}, z, {
          scheduled, max, overflow, statusClass, statusLabel,
        })
      }).sort((a, b) => b.scheduled - a.scheduled)
    },
  },
  methods: {
    zoneCardClass(z) {
      return {
        'rts-zone-card-over': z.overflow > 0,
        'rts-zone-card-off':  z.statusLabel === 'ZONE OFF',
      }
    },
  },
}
</script>

<style scoped>
.rts-day-detail { width: 100%; }

.rts-toolbar {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 20px; padding: 16px 24px; background: white;
  border-radius: 16px; box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  flex-wrap: wrap; gap: 14px;
}
.rts-toolbar-left, .rts-toolbar-right { display: flex; align-items: center; gap: 14px; }
.rts-btn-back {
  background: #edf2f7; border: none; border-radius: 10px; padding: 12px 20px;
  cursor: pointer; font-weight: 800; font-size: 15px; color: #2d3748;
  display: inline-flex; align-items: center; gap: 8px;
}
.rts-badge { padding: 12px 22px; border-radius: 24px; color: white;
  font-weight: 900; font-size: 15px; letter-spacing: 0.5px;
  display: inline-flex; align-items: center; gap: 8px; }
.rts-badge-hd { background: #4299e1; }
.rts-badge-sp { background: #9c27b0; }
.rts-week-label { font-weight: 800; color: #2d3748; font-size: 20px; }
.rts-nav-btn {
  background: white; border: 2px solid #cbd5e0; border-radius: 10px;
  padding: 12px 20px; cursor: pointer; font-weight: 800; font-size: 15px;
  color: #2d3748; display: inline-flex; align-items: center; gap: 8px;
}
.rts-nav-btn:hover { background: #f7fafc; border-color: #4299e1; color: #2b6cb0; }

.rts-stats-grid {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px; margin-bottom: 20px;
}
.rts-stat-card {
  display: flex; align-items: center; gap: 20px;
  padding: 24px 28px; border-radius: 18px; color: white;
  box-shadow: 0 8px 20px rgba(0,0,0,0.12);
  position: relative; overflow: hidden;
}
.rts-stat-card::before {
  content: ''; position: absolute; top: -50%; right: -20%;
  width: 200px; height: 200px; background: rgba(255,255,255,0.1);
  border-radius: 50%;
}
.rts-stat-icon {
  width: 68px; height: 68px; border-radius: 16px;
  background: rgba(255,255,255,0.2); display: flex; align-items: center;
  justify-content: center; font-size: 32px; flex-shrink: 0;
  position: relative; z-index: 1;
}
.rts-stat-body { position: relative; z-index: 1; }
.rts-stat-value { font-size: 44px; font-weight: 900; line-height: 1; margin-bottom: 6px; }
.rts-stat-label { font-size: 14px; font-weight: 800; text-transform: uppercase;
  letter-spacing: 1px; opacity: 0.95; }
.rts-stat-blue   { background: linear-gradient(135deg, #4299e1, #2b6cb0); }
.rts-stat-teal   { background: linear-gradient(135deg, #38b2ac, #285e61); }
.rts-stat-green  { background: linear-gradient(135deg, #48bb78, #2f855a); }
.rts-stat-red    { background: linear-gradient(135deg, #f56565, #c53030); }
.rts-stat-gray   { background: linear-gradient(135deg, #a0aec0, #4a5568); }

.rts-day-actions { margin-bottom: 20px; }
.rts-action-btn {
  border: none; border-radius: 12px; padding: 14px 22px;
  font-size: 15px; font-weight: 900; letter-spacing: 0.5px;
  cursor: pointer; display: inline-flex; align-items: center; gap: 10px;
  text-transform: uppercase; box-shadow: 0 4px 10px rgba(0,0,0,0.12);
}
.rts-action-distribute {
  background: linear-gradient(135deg, #9f7aea, #805ad5);
  color: white;
}
.rts-action-distribute:hover {
  transform: translateY(-2px); box-shadow: 0 8px 16px rgba(0,0,0,0.18);
}

.rts-zone-breakdown {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 18px;
}
.rts-zone-card {
  background: white; border-radius: 14px; border: 2px solid #e2e8f0; overflow: hidden;
}
.rts-zone-card-over { border-color: #fc8181; }
.rts-zone-card-off  { opacity: 0.65; }
.rts-zone-card-empty {
  grid-column: 1 / -1; text-align: center; padding: 40px;
  color: #a0aec0; border-style: dashed;
}
.rts-zone-card-empty i { font-size: 48px; opacity: 0.4; margin-bottom: 12px; display: block; }
.rts-zone-card-head {
  padding: 12px 18px; color: white;
  display: flex; align-items: center; gap: 12px;
}
.rts-zone-code { font-weight: 900; font-size: 16px; letter-spacing: 0.5px; }
.rts-zone-name { font-weight: 700; font-size: 13px; opacity: 0.9; flex: 1; }
.rts-zone-cap { font-weight: 900; font-size: 15px; }
.rts-zone-card-body { padding: 14px 18px; }
.rts-zone-status {
  font-size: 12px; font-weight: 900; letter-spacing: 0.5px;
  padding: 4px 10px; border-radius: 8px; display: inline-block; margin-bottom: 10px;
}
.rts-zone-ok        { background: #c6f6d5; color: #22543d; }
.rts-zone-full      { background: #fefcbf; color: #744210; }
.rts-zone-over      { background: #fed7d7; color: #822727; }
.rts-zone-off       { background: #e2e8f0; color: #4a5568; }
.rts-zone-unlimited { background: #bee3f8; color: #2c5282; }
.rts-zone-overflow { color: #c53030; font-weight: 800; font-size: 13px; margin-bottom: 10px; }
.rts-zone-orders {
  display: flex; flex-direction: column; gap: 8px;
  max-height: 320px; overflow-y: auto;
}
.rts-zone-empty {
  text-align: center; color: #a0aec0; font-style: italic;
  padding: 20px; font-size: 13px;
}
.rts-order-mini {
  background: #f7fafc; border-radius: 10px; padding: 10px 12px;
  border: 1px solid #e2e8f0;
}
.rts-order-mini-top {
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;
}
.rts-order-mini-inv { font-weight: 900; font-size: 14px; color: #1a202c; }
.rts-order-mini-type {
  font-size: 10px; font-weight: 900; background: #edf2f7; color: #4a5568;
  padding: 2px 6px; border-radius: 6px; text-transform: uppercase;
}
.rts-order-mini-name { font-weight: 700; font-size: 13px; color: #2d3748; }
.rts-order-mini-addr { font-size: 12px; color: #718096; margin-top: 2px; }
.rts-order-mini-resched {
  margin-top: 8px; width: 100%; background: #edf2f7; border: none;
  border-radius: 8px; padding: 6px; font-size: 11px; font-weight: 900;
  text-transform: uppercase; letter-spacing: 0.5px; cursor: pointer; color: #4a5568;
}
.rts-order-mini-resched:hover { background: #e2e8f0; }
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/DayDetail.vue